import { Inject, Injectable } from '@angular/core';
import {
  LiteratureLabelCollection,
  LiteratureLabels,
} from '@literature/literature-label';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { WINDOW } from '@ng-web-apis/common';
import { PersonalisationAPIService, SegmentService } from '@services';
import {
  Audience,
  ItemDetail,
  LiteratureContentDocument,
  LiteratureData,
  LiteratureDocument,
  LiteratureDocumentData,
  SegmentId,
} from '@types';
import capitalize from 'lodash/capitalize';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

@Injectable()
export class ItemDetailService {
  cartDocIds: string[] = [];
  isIdentified = false;

  constructor(
    @Inject(WINDOW) readonly windowRef: Window,
    private literatureDataService: LiteratureDataService,
    protected segmentService: SegmentService,
    protected cartService: CartHandlerService,
    private personalisationService: PersonalisationAPIService,
    private literatureConfigService?: LiteratureGlobalConfigService
  ) {
    this.personalisationService
      .isIdentified$()
      .subscribe((isIdentified) => (this.isIdentified = isIdentified));
  }

  /**
   *
   * @param params map to call literature api consists of docID
   * @returns Observable of literature document object
   * create side effects to modify the response
   */
  populate(params: Map<string, string>): Observable<ItemDetail> {
    return this.literatureDataService.loadData(params, true, true).pipe(
      map((data: LiteratureData) =>
        this.modifyItemDetailData(data.document[0])
      ),
      map((data: ItemDetail) => this.checkActorAndLoginDetails(data)),
      shareReplay(1)
    );
  }

  /**
   *
   * @param code - id to qualify for open account button visiblity
   * @returns boolean based on if account config is found
   * account config iscoming from labels of BR
   * we search the code in labels
   * this method ensures the visibilty of ADD TO ACCOUNT opeartion
   */
  getConfig(code: string): boolean {
    const config = LiteratureLabels.getLabels(
      LiteratureLabelCollection.accountConfig
    );
    if (config) {
      return code in config;
    }
    return false;
  }

  goToLink(url: string) {
    this.windowRef.open(url, '_blank');
  }

  /**
   *
   * @param data - LiteratureDocumentData object
   * @returns modified literature object
   * map the object through literature mapper
   * load download urls in literature object
   * check for audience as needed to show/hide client use details in paged based on that info
   */
  modifyItemDetailData(data: LiteratureDocumentData): LiteratureDocument {
    const detailData = this.literatureDataService.literatureMapper(data);
    this.literatureDataService.mapLiteratureUrl(detailData);
    if (SegmentId.INVESTOR === this.segmentService.getCurrentSegmentId()) {
      detailData.isInvestor = true;
    }
    return detailData;
  }

  /**
   *
   * @param literatureCode - docId
   * @param literatureContentDocuments - array of all availble content from BR- document content config
   * @returns LiteratureContentDocument based on search of literatureCode in list of available content
   * insure the visibilty of open account action
   * BR config is needed to show open account button
   */
  isLiteratureAddOnAccount(
    literatureCode: string,
    literatureContentDocuments: LiteratureContentDocument[]
  ): LiteratureContentDocument {
    return this.literatureDataService.isLiteratureAddOnAccount(
      literatureCode,
      literatureContentDocuments
    );
  }

  /**
   *
   * @param data literature object
   * @returns modified literature object
   * modify the object based on login/non logged user and based of actors
   * set metadata of object to show some alert on page
   * this insures the direct access of detail page (cross actor access/ user role access) prevention
   * restrict the visibilty of info on page based on rules
   */
  checkActorAndLoginDetails(data: ItemDetail) {
    const userProfile = this.literatureConfigService.getUserProfile();
    if (
      (!data.clientUse || data.clientUse === 'No') &&
      !userProfile.isLoggedIn &&
      !this.isIdentified
    ) {
      data.loginRequired = true;
    }
    return data;
  }
}
